// Reading and writing project files. GPL-3.0-or-later.
import { PROJECT_FORMAT, PROJECT_VERSION, UNITS, type Project } from "./schema.js";
import { isObj, sanitize } from "./sanitize.js";
import { fromDipField1, fromDipFieldSlope1 } from "./migrate.js";

export type Source = "suite" | "dipfield" | "dipfield-slope";
/** Why a file could not be opened. `code` is stable; the app words it. */
export class ProjectError extends Error {
  constructor(public code: "not-json" | "not-a-project" | "newer-version" | "units", message: string) { super(message); }
}

/** Open any project file the Suite understands: its own (v2), DipField 1.x and
 *  DipField Slope 0.1. Older files are migrated; `project.meta.migratedFrom` says how. */
export function readProject(input: string | unknown, now = new Date().toISOString()): { project: Project; source: Source } {
  let j: unknown = input;
  if (typeof input === "string") {
    try { j = JSON.parse(input.replace(/^﻿/, "")); } catch { throw new ProjectError("not-json", "not a JSON file"); }
  }
  if (!isObj(j)) throw new ProjectError("not-a-project", "not a project file");
  if (j.format === PROJECT_FORMAT) {
    const v = Number(j.version);
    if (v > PROJECT_VERSION) throw new ProjectError("newer-version", `project version ${v} is newer than this app reads (${PROJECT_VERSION})`);
    if (isObj(j.units) && Object.entries(UNITS).some(([k, u]) => (j.units as Record<string, unknown>)[k] !== u)) throw new ProjectError("units", "the file states units other than kN, kPa, m");
    return { project: sanitize(j), source: "suite" };
  }
  if (j.app === "DipField" && Array.isArray(j.planes)) return { project: fromDipField1(j, now), source: "dipfield" };
  if (j.app === "DipField Slope" && isObj(j.state)) return { project: fromDipFieldSlope1(j, now), source: "dipfield-slope" };
  throw new ProjectError("not-a-project", "not a DipField Suite, DipField or DipField Slope project");
}

/** The file text. `savedBy` and `engines` record what produced the results in it. */
export function writeProject(p: Project, o: { app?: { app: string; version: string }; engines?: Record<string, string>; now?: string } = {}): string {
  const out: Project = {
    ...p,
    meta: { ...p.meta, savedAt: o.now ?? new Date().toISOString(), savedBy: o.app ?? p.meta.savedBy, engines: o.engines ?? p.meta.engines },
    units: { ...UNITS },
  };
  return JSON.stringify(out, null, 1) + "\n";
}

/** File name for a project: its name, made safe for every file system. */
export function fileName(p: Project, ext = ".dfsuite.json"): string {
  const base = p.meta.name.normalize("NFC").replace(/[\\/:*?"<>|\u0000-\u001f]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 80) || "project";
  return base + ext;
}
